import { useState } from 'react'
import { trpc } from '@/config/trpc'
import { type Event } from '@/lib/calendar/types'
import { scheduledPostToEvent, trackedPostToEvent } from '@/lib/calendar/post-adapter'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'
import { Button } from '@/components/ui/shadcn/button'
import { Badge } from '@/components/ui/shadcn/badge'
import { Link } from 'react-router'
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import { ExternalLink, Clock, Check, AlertCircle, Download, Plus, Inbox } from 'lucide-react'

type StatusFilter = 'all' | 'scheduled' | 'published' | 'imported' | 'failed'
type PlatformFilter = 'all' | PublishablePlatformType
type EventStatus = NonNullable<Event['status']>

interface MyPostsListProps {
  status: StatusFilter
  platform: PlatformFilter
  onSelectEvent: (event: Event) => void
  onImport?: () => void
}

const platformIcons: Record<string, typeof InstagramIcon> = {
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  tiktok: TikTokIcon,
  youtube: YoutubeIcon,
  facebook: FacebookIcon,
}

export function MyPostsList({ status, platform, onSelectEvent, onImport }: MyPostsListProps) {
  const [visibleCount, setVisibleCount] = useState(50)

  const { data: scheduledPosts, isLoading: loadingScheduled } = trpc.scheduledPost.list.useQuery()
  const { data: trackedPosts, isLoading: loadingTracked } = trpc.trackedPost.list.useQuery(
    platform !== 'all' ? { platform } : undefined,
  )

  const isLoading = loadingScheduled || loadingTracked

  const matchesPlatform = (p: PublishablePlatformType) => platform === 'all' || p === platform

  const events: Event[] = []
  for (const sp of scheduledPosts ?? []) {
    if (sp.status === 'published') continue
    if (!matchesPlatform(sp.platform)) continue
    events.push(scheduledPostToEvent(sp))
  }
  for (const tp of trackedPosts ?? []) {
    if (!matchesPlatform(tp.platform as PublishablePlatformType)) continue
    events.push(trackedPostToEvent(tp))
  }

  const filtered = events
    .filter((e) => {
      if (status === 'all') return true
      if (status === 'scheduled') return e.status === 'scheduled' || e.status === 'publishing'
      return e.status === status
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime())

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="border-primary/30 border-t-primary h-7 w-7 animate-spin rounded-full border-2" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {filtered.length === 0 ? (
        events.length > 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
              <Inbox size={20} className="text-muted-foreground/40" />
            </div>
            <div>
              <p className="text-foreground font-medium">No posts match your filters</p>
              <p className="text-muted-foreground mt-0.5 text-sm">
                Try adjusting the status or platform filter above
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <button
              onClick={onImport}
              disabled={!onImport}
              className="bg-muted hover:bg-hover disabled:hover:bg-muted flex h-12 w-12 items-center justify-center rounded-full transition-colors disabled:cursor-default">
              <Plus size={20} className="text-muted-foreground/50" />
            </button>
            <div>
              <p className="text-foreground font-medium">No posts yet</p>
              <p className="text-muted-foreground mt-0.5 text-sm">
                {onImport ? (
                  <button onClick={onImport} className="text-primary hover:underline">
                    Import posts
                  </button>
                ) : (
                  'Import existing posts or schedule a new one to get started'
                )}
              </p>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/account/social-media">Connect Platforms</Link>
            </Button>
          </div>
        )
      ) : (
        <>
          <div className="flex flex-col">
            {filtered.slice(0, visibleCount).map((event) => (
              <PostRow key={event.id} event={event} onClick={() => onSelectEvent(event)} />
            ))}
          </div>
          {filtered.length > visibleCount && (
            <div className="mt-2 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setVisibleCount((c) => c + 50)}>
                Show more ({filtered.length - visibleCount} remaining)
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function PostRow({ event, onClick }: { event: Event; onClick: () => void }) {
  const PlatformIcon = platformIcons[event.platform]
  const thumbnail = getEventThumbnail(event)

  return (
    <div
      className="hover:bg-hover border-border/60 flex cursor-pointer items-center gap-4 rounded-lg border-t px-3 py-3 transition-colors first:border-t-0"
      onClick={onClick}>
      <div className="bg-card border-border flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border">
        {thumbnail ? (
          thumbnail.isVideo ? (
            // `#t=0.1` asks for a frame just past the start; some browsers
            // render nothing for a bare video src until one is seeked to.
            <video
              src={`${thumbnail.url}#t=0.1`}
              className="h-full w-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={thumbnail.url}
              alt=""
              className="h-full w-full object-cover"
              crossOrigin="anonymous"
            />
          )
        ) : PlatformIcon ? (
          <PlatformIcon width={20} height={20} color="var(--foreground)" />
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-0.5 flex items-center gap-1.5">
          {PlatformIcon && <PlatformIcon width={13} height={13} color="var(--muted-foreground)" />}
          <span className="text-muted-foreground text-xs capitalize">{event.platform}</span>
          {event.status && <StatusBadge status={event.status} />}
        </div>
        <p className="text-foreground truncate text-sm leading-snug">{event.title}</p>
        <p className="text-muted-foreground/60 mt-0.5 text-xs">
          {event.date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      </div>

      {event.link && (
        <a
          href={event.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
          onClick={(e) => e.stopPropagation()}>
          <ExternalLink size={14} />
        </a>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: EventStatus }) {
  switch (status) {
    case 'published':
      return (
        <Badge
          variant="secondary"
          className="bg-green-100 px-1.5 py-0 text-[10px] text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <Check size={10} className="mr-0.5" />
          Published
        </Badge>
      )
    case 'imported':
      return (
        <Badge
          variant="secondary"
          className="bg-blue-100 px-1.5 py-0 text-[10px] text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <Download size={10} className="mr-0.5" />
          Imported
        </Badge>
      )
    case 'scheduled':
      return (
        <Badge
          variant="secondary"
          className="bg-yellow-100 px-1.5 py-0 text-[10px] text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
          <Clock size={10} className="mr-0.5" />
          Scheduled
        </Badge>
      )
    case 'publishing':
      return (
        <Badge
          variant="secondary"
          className="bg-orange-100 px-1.5 py-0 text-[10px] text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
          <Clock size={10} className="mr-0.5" />
          Publishing
        </Badge>
      )
    case 'failed':
      return (
        <Badge
          variant="secondary"
          className="bg-red-100 px-1.5 py-0 text-[10px] text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle size={10} className="mr-0.5" />
          Failed
        </Badge>
      )
    case 'draft':
      return (
        <Badge
          variant="secondary"
          className="bg-slate-100 px-1.5 py-0 text-[10px] text-slate-700 dark:bg-slate-900/30 dark:text-slate-400">
          Draft
        </Badge>
      )
  }
}

/** A video post's `url` is the video file, not a still, so an `<img>` has
 *  nothing to decode and renders an empty box. The caller needs the media kind
 *  to pick the right element, the same way ListCalendar does. */
function getEventThumbnail(event: Event): { url: string; isVideo: boolean } | null {
  const content = event.postContent

  const fromContent = (() => {
    if (!content) return null
    if ('url' in content && typeof content.url === 'string') {
      const isVideo =
        content.type === 'video' || content.type === 'reel' || content.type === 'videoStory'
      return { url: content.url, isVideo }
    }
    if ('urls' in content && Array.isArray(content.urls) && content.urls.length > 0) {
      const first = content.urls[0]
      if (typeof first === 'string') return { url: first, isVideo: false }
      if (typeof first === 'object' && first && 'url' in first) {
        return { url: first.url as string, isVideo: 'type' in first && first.type === 'video' }
      }
    }
    return null
  })()

  // `mediaS3Url` is described as a thumbnail image, but for posts published
  // through CASSI it is set to the media itself, the same URL the content
  // carries. Only treat it as a still when it points somewhere else — an
  // imported post's genuine thumbnail.
  if (event.mediaUrl) {
    const isTheMediaItself = event.mediaUrl === fromContent?.url
    return { url: event.mediaUrl, isVideo: isTheMediaItself && fromContent.isVideo }
  }

  return fromContent
}
